import { ArrowRight, ArrowLeft, CheckCircle2, Check, X } from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDuckApplications, updateDuckApplication } from "../../services/duckService";

const duckLevels = [
  {
    id: "junior_general",
    title: "Junior duck application",
    subtitle: "General",
    description: "We all start somewhere",
    level: "Junior Duck",
    disabled: false,
    requirements: [
      {
        title: "Community engagement",
        description: "Active participation in at least 3 community events",
        importance: "required",
      },
      {
        title: "Reviews and recommendations",
        description: "At least 2 positive reviews from community members",
        importance: "required",
      },
      {
        title: "Communication skills",
        description: "Basic communication skills in English or local language",
        importance: "required",
      },
      {
        title: "Platform familiarity",
        description: "Familiarity with our platform and community guidelines",
        importance: "recommended",
      },
    ],
  },
  {
    id: "skilled_skiing",
    title: "Skilled duck application",
    subtitle: "Skiing / Snowboarding",
    description: "Coming soon - Group leader with maximum aura",
    level: "Skilled Duck",
    disabled: true,
    requirements: [
      {
        title: "Skiing/snowboarding certification",
        description: "Valid instructor certification or equivalent experience",
        importance: "required",
      },
      {
        title: "Safety training",
        description: "Current first aid and mountain safety certification",
        importance: "required",
      },
      {
        title: "Experience level",
        description: "Minimum 3 years of skiing/snowboarding experience",
        importance: "required",
      },
      {
        title: "Previous leadership",
        description: "Experience leading at least 5 group sessions",
        importance: "recommended",
      },
      {
        title: "Equipment knowledge",
        description: "Deep understanding of equipment and maintenance",
        importance: "required",
      },
    ],
  },
  {
    id: "skilled_hiking",
    title: "Skilled duck application",
    subtitle: "Hiking",
    description: "Coming soon - Group leader with maximum aura",
    level: "Skilled Duck",
    disabled: true,
    requirements: [
      {
        title: "Trail experience",
        description: "Completed at least 10 different hiking trails",
        importance: "required",
      },
      {
        title: "Navigation skills",
        description: "Proficient in map reading and navigation tools",
        importance: "required",
      },
      {
        title: "Safety certification",
        description: "Valid wilderness first aid certification",
        importance: "required",
      },
      {
        title: "Physical fitness",
        description: "Ability to lead challenging trails and assist others",
        importance: "required",
      },
      {
        title: "Local knowledge",
        description: "Familiarity with local trails and conditions",
        importance: "recommended",
      },
    ],
  },
];

const VideoSection = ({ title, description }) => {
  return (
    <div className="tw-w-full tw-max-w-xl tw-space-y-6">
      <div className="tw-aspect-video tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-gray-100">
        {/* Placeholder for video */}
        <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
          <p className="tw-text-gray-500">Video content will be added here</p>
        </div>
      </div>
      <div className="tw-space-y-2">
        <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">{title}</h3>
        <p className="tw-text-sm tw-text-gray-500">{description}</p>
      </div>
    </div>
  );
};

const SuccessView = () => {
  const [showContent, setShowContent] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showLevels, setShowLevels] = useState(false);

  useEffect(() => {
    // Sequence the animations
    const sequence = async () => {
      await new Promise((resolve) => setTimeout(resolve, 500)); // Initial delay
      setShowContent(true);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for duck animation
      setShowProgress(true);
      await new Promise((resolve) => setTimeout(resolve, 800)); // Wait for progress bar
      setShowLevels(true);
    };

    sequence();
  }, []);

  return (
    <div className="tw-w-full tw-max-w-xl tw-space-y-12 tw-text-center">
      {/* Duck Icon with Entrance Animation */}
      <motion.div
        className="tw-flex tw-justify-center"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: "spring", bounce: 0.4 }}
      >
        <motion.img
          src={require("../../assets/images/sotravel-duck.png")}
          alt="Sotravel Duck"
          className="tw-h-72 tw-w-72 tw-object-contain"
          animate={{
            y: [0, -20, 0],
            rotate: [0, -5, 5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
        />
      </motion.div>

      {/* Title with Stagger Animation */}
      <AnimatePresence>
        {showContent && (
          <motion.div
            className="tw-space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="tw-bg-gradient-to-r tw-from-primary-main tw-to-yellow-500 tw-bg-clip-text tw-text-4xl tw-font-bold tw-text-transparent">
                Junior duck achieved
              </h2>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress Section */}
      <div className="tw-space-y-8">
        <AnimatePresence>
          {showProgress && (
            <motion.div
              className="tw-space-y-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-500">
                <span>Level Progress</span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  75%
                </motion.span>
              </div>
              <div className="tw-h-2 tw-overflow-hidden tw-rounded-full tw-bg-white/10">
                <motion.div
                  className="tw-h-full tw-rounded-full tw-bg-primary-main"
                  initial={{ width: "0%" }}
                  animate={{ width: "75%" }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
              <motion.div
                className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <span>Senior duck</span>
                <span>→ Super duck</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Level Indicators with Stagger */}
        <AnimatePresence>
          {showLevels && (
            <motion.div
              className="tw-flex tw-justify-between"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {["Duckling", "Junior", "Senior", "Super"].map((level, index) => (
                <motion.div
                  key={level}
                  className={`tw-flex tw-flex-col tw-items-center tw-gap-2 ${
                    index <= 1
                      ? "tw-text-primary-main"
                      : index === 2
                        ? "tw-text-yellow-500"
                        : "tw-text-white/40"
                  }`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <motion.div
                    className={`tw-h-3 tw-w-3 tw-rounded-full ${
                      index <= 1
                        ? "tw-bg-primary-main"
                        : index === 2
                          ? "tw-bg-yellow-500"
                          : "tw-bg-white/40"
                    }`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: index * 0.2,
                      type: "spring",
                      bounce: 0.6,
                    }}
                  />
                  <motion.span
                    className="tw-text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.2 + 0.2 }}
                  >
                    {level}
                  </motion.span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const QuizResults = ({ quizQuestions, answers, onRetake }) => {
  const totalQuestions = quizQuestions.reduce((acc, section) => acc + section.questions.length, 0);
  const correctAnswers = quizQuestions.reduce((acc, section) => {
    return (
      acc +
      section.questions.reduce((sectionAcc, question) => {
        const isCorrect = question.multiSelect
          ? JSON.stringify(answers[question.id]?.sort()) ===
            JSON.stringify(question.correctAnswers.sort())
          : answers[question.id] === question.correctAnswers[0];
        return sectionAcc + (isCorrect ? 1 : 0);
      }, 0)
    );
  }, 0);

  const passThreshold = 0.7; // 70% to pass
  const hasPassedQuiz = correctAnswers / totalQuestions >= passThreshold;

  return (
    <div className="tw-w-full tw-max-w-xl tw-space-y-8">
      {/* Overall Score */}
      <div className="tw-rounded-xl tw-bg-gray-50 tw-p-6 tw-text-center">
        <h3 className="tw-text-2xl tw-font-bold">
          {hasPassedQuiz ? "Congratulations! 🎉" : "Almost there!"}
        </h3>
        <p className="tw-mt-2 tw-text-gray-500">
          You got {correctAnswers} out of {totalQuestions} questions correct
        </p>
        <div className="tw-mt-4 tw-h-2 tw-rounded-full tw-bg-white/10">
          <div
            className={`tw-h-full tw-rounded-full ${
              hasPassedQuiz ? "tw-bg-green-500" : "tw-bg-yellow-500"
            }`}
            style={{ width: `${(correctAnswers / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Section Breakdown */}
      <div className="tw-space-y-6">
        {quizQuestions.map((section, sectionIndex) => (
          <div key={section.id} className="tw-rounded-xl tw-bg-gray-50 tw-p-6">
            <h4 className="tw-mb-4 tw-text-lg tw-font-semibold">{section.title}</h4>
            <div className="tw-space-y-4">
              {section.questions.map((question, questionIndex) => {
                const isCorrect = question.multiSelect
                  ? JSON.stringify(answers[question.id]?.sort()) ===
                    JSON.stringify(question.correctAnswers.sort())
                  : answers[question.id] === question.correctAnswers[0];

                return (
                  <div
                    key={question.id}
                    className={`tw-rounded-lg tw-p-4 ${
                      isCorrect ? "tw-bg-green-500/10" : "tw-bg-red-500/10"
                    }`}
                  >
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <div
                        className={`tw-mt-1 tw-rounded-full tw-p-1 ${
                          isCorrect ? "tw-bg-green-500/20" : "tw-bg-red-500/20"
                        }`}
                      >
                        {isCorrect ? (
                          <Check className="tw-h-4 tw-w-4 tw-text-green-500" />
                        ) : (
                          <X className="tw-h-4 tw-w-4 tw-text-red-500" />
                        )}
                      </div>
                      <div>
                        <p className="tw-font-medium">{question.question}</p>
                        {!isCorrect && (
                          <p className="tw-mt-2 tw-text-sm tw-text-red-400">
                            Please review this question. Consider rewatching the video material for
                            better understanding.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!hasPassedQuiz && (
          <>
            <button
              onClick={onRetake}
              className="tw-rounded-xl tw-bg-primary-main tw-px-6 tw-py-3 tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90"
            >
              Retake Quiz
            </button>
            <p className="tw-text-center tw-text-sm tw-text-gray-500">
              Tip: Rewatch the video materials before retaking the quiz to better understand the
              concepts
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const QuizQuestion = ({ questions, selectedAnswers, onSelect, startingNumber = 1 }) => {
  return (
    <div className="tw-w-full tw-max-w-xl tw-space-y-12 tw-pb-8 md:tw-pb-12">
      {questions.map((question, qIndex) => (
        <div key={question.id} className="tw-space-y-6">
          <div className="tw-space-y-2">
            <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">
              {startingNumber + qIndex}. {question.question}
            </h3>
            {question.multiSelect && (
              <p className="tw-text-sm tw-text-gray-500">Select all that apply</p>
            )}
          </div>
          <div className="tw-space-y-4">
            {question.options.map((option) => {
              const isSelected = question.multiSelect
                ? selectedAnswers[question.id]?.includes(option.id)
                : selectedAnswers[question.id] === option.id;

              return (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (question.multiSelect) {
                      const currentAnswers = selectedAnswers[question.id] || [];
                      const newAnswers = isSelected
                        ? currentAnswers.filter((id) => id !== option.id)
                        : [...currentAnswers, option.id];
                      onSelect(question.id, newAnswers);
                    } else {
                      onSelect(question.id, option.id);
                    }
                  }}
                  className={`tw-group tw-w-full tw-rounded-xl tw-p-6 tw-text-left tw-transition-all tw-duration-200 ${
                    isSelected
                      ? "tw-bg-primary-main tw-text-white"
                      : "tw-bg-gray-50 hover:tw-bg-gray-100"
                  }`}
                >
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      {isSelected ? (
                        <div className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-md tw-bg-white">
                          <Check className="tw-h-4 tw-w-4 tw-text-primary-main" />
                        </div>
                      ) : (
                        <div className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-white/20" />
                      )}
                      <p className="tw-text-lg tw-font-medium">{option.text}</p>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const GroupLeaderApplication = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    programType: "",
    quizAnswers: {},
    showingResults: false,
    has_acknowledged_prereqs: false,
    currentStep: 0,
  });

  // Debounce save timer
  const saveTimerRef = useRef(null);

  // Save progress with debounce
  const saveProgress = useCallback(async (newData, isComplete = false) => {
    const data = {
      duck_type: newData.programType,
      quiz_answers: newData.quizAnswers,
      last_step: newData.currentStep,
      has_acknowledged_prereqs: newData.has_acknowledged_prereqs,
      ...(isComplete && { status: "completed", quiz_score: newData.quizScore }),
    };

    try {
      await updateDuckApplication(data);
    } catch (error) {
      // Silent fail for optimistic updates
      console.error("Error saving progress:", error);
    }
  }, []);

  // Debounced save function
  const debouncedSave = useCallback(
    (newData, isComplete = false) => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(() => {
        saveProgress(newData, isComplete);
      }, 1000); // 1 second debounce
    },
    [saveProgress]
  );

  const quizQuestions = useMemo(
    () => [
      {
        id: "understanding_sotravel",
        title: "Understanding Sotravel",
        questions: [
          {
            id: "primary_goal",
            question: "What's the primary goal of Sotravel trips?",
            options: [
              { id: "follow_itinerary", text: "To make sure the itinerary is followed strictly" },
              { id: "sell_trips", text: "To sell as many trips as possible" },
              { id: "build_community", text: "To build a community through shared adventure" },
              { id: "promote_vendors", text: "To promote local vendors" },
            ],
            correctAnswers: ["sell_trips"],
            multiSelect: true,
          },
          {
            id: "core_values",
            question: "Which one of these is not part of Sotravel's core values?",
            options: [
              { id: "community", text: "Community" },
              { id: "compliance", text: "Compliance" },
              { id: "leadership", text: "Leadership" },
              { id: "adventure", text: "Adventure" },
            ],
            correctAnswers: ["compliance"],
            multiSelect: true,
          },
        ],
      },
      {
        id: "trip_leader_role",
        title: "Trip leader role",
        questions: [
          {
            id: "leader_role",
            question: "Which of the following best describes the role of a trip leader?",
            options: [
              { id: "social_glue", text: "A social glue who facilitates connection and vibe" },
              { id: "decision_maker", text: "The one who always decides what the group should do" },
              {
                id: "schedule_keeper",
                text: "The person who ensures everyone sticks to the schedule",
              },
              { id: "tour_guide", text: "A tour guide with all the answers" },
            ],
            correctAnswers: ["schedule_keeper"],
            multiSelect: true,
          },
          {
            id: "quiet_participants",
            question: "What's one way to make the quieter participants feel more included?",
            options: [
              { id: "lead_activity", text: "Ask them to lead the group activity" },
              { id: "focus_outgoing", text: "Keep the focus on more outgoing participants" },
              { id: "one_on_one", text: "Pull them aside for a 1-on-1 talk" },
              { id: "casual_opinions", text: "Casually ask their opinions during group moments" },
            ],
            correctAnswers: ["casual_opinions"],
            multiSelect: true,
          },
        ],
      },
      {
        id: "trip_management",
        title: "Trip management",
        questions: [
          {
            id: "pre_trip",
            question: "What's the one thing that a trip leader should do before a trip starts?",
            options: [
              { id: "weather_report", text: "Create a full weather report for the destination" },
              { id: "local_language", text: "Learn the common phrases in the local language" },
              {
                id: "itinerary_basics",
                text: "Familiarize yourself with the itinerary and destination basics",
              },
              { id: "packing_list", text: "Create a packing list for the participants" },
            ],
            correctAnswers: ["local_language"],
            multiSelect: true,
          },
          {
            id: "expenses",
            question: "Where should the expenses for claiming be recorded?",
            options: [
              { id: "sotravel_app", text: "On the Sotravel app" },
              { id: "email_admin", text: "Email to Sotravel admin" },
              { id: "splitwise", text: "Splitwise" },
            ],
            correctAnswers: ["email_admin"],
            multiSelect: true,
          },
          {
            id: "itinerary_change",
            question:
              "During the trip, if the group wants to stop for an hour at a market that's not in the itinerary, what should you do?",
            options: [
              { id: "vibe_and_time", text: "Decide based on whether it fits the vibe and time" },
              { id: "do_both", text: "Insist on doing both the market and the planned activity" },
              { id: "ask_hq", text: "Ask HQ for approval before changing plans" },
              { id: "follow_itinerary", text: "Say no, the itinerary must be followed" },
            ],
            correctAnswers: ["vibe_and_time"],
            multiSelect: true,
          },
        ],
      },
      {
        id: "group_dynamics",
        title: "Group dynamics",
        questions: [
          {
            id: "no_consensus",
            question: "What if the group is not able to come to a consensus?",
            options: [
              {
                id: "individual_choice",
                text: "Let everyone do what they want, but stay updated on the group chat",
              },
              { id: "convince_minority", text: "Try to convince the minority to follow" },
              { id: "cancel_optional", text: "Cancel all optional plans" },
              { id: "find_alternative", text: "Find another option that everyone is okay with" },
            ],
            correctAnswers: ["cancel_optional"],
            multiSelect: true,
          },
          {
            id: "post_trip",
            question: "What should be done after the trip? (Select all that apply)",
            options: [
              { id: "photos_videos", text: "Pictures and videos taken" },
              { id: "trip_review", text: "Review of the trip on Sotravel's app" },
              { id: "participant_review", text: "Review of other participants on Sotravel's app" },
              { id: "instagram", text: "Sharing instagram handles" },
              { id: "recommend", text: "Recommending the trip to your other friends" },
            ],
            correctAnswers: ["photos_videos", "trip_review"],
            multiSelect: true,
          },
        ],
      },
    ],
    []
  );

  const handleRetakeQuiz = useCallback(() => {
    setFormData((prev) => ({ ...prev, currentStep: 3 })); // Go back to the first quiz section
  }, []);

  const steps = useMemo(() => {
    // Memoize steps array
    return [
      {
        title: "Which program interests you?",
        subtitle: "Choose the program that best matches your skills and interests",
        content: (
          <div className="tw-w-full tw-max-w-xl tw-space-y-4">
            {duckLevels.map((program) => (
              <motion.button
                key={program.id}
                whileHover={{ scale: program.disabled ? 1 : 1.02 }}
                whileTap={{ scale: program.disabled ? 1 : 0.98 }}
                onClick={() => {
                  if (!program.disabled) {
                    const newFormData = {
                      ...formData,
                      programType: program.id,
                      has_acknowledged_prereqs: false,
                    };
                    setFormData(newFormData);
                    debouncedSave(newFormData);
                  }
                }}
                className={`tw-group tw-w-full tw-rounded-xl tw-p-6 tw-text-left tw-transition-all tw-duration-200 ${
                  formData.programType === program.id
                    ? "tw-bg-primary-main tw-text-white"
                    : program.disabled
                      ? "tw-cursor-not-allowed tw-bg-gray-50 tw-opacity-50"
                      : "tw-bg-gray-50 hover:tw-bg-gray-100"
                }`}
              >
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div>
                    <h3 className="tw-text-2xl tw-font-bold">{program.title}</h3>
                    <p
                      className={`tw-mt-1 tw-text-sm tw-font-medium ${
                        formData.programType === program.id
                          ? "tw-text-white/80"
                          : "tw-text-gray-500"
                      }`}
                    >
                      {program.subtitle}
                    </p>
                  </div>
                  <ArrowRight
                    className={`tw-h-6 tw-w-6 tw-transition-transform group-hover:tw-translate-x-1 ${
                      formData.programType === program.id ? "tw-text-white" : "tw-text-gray-500"
                    }`}
                  />
                </div>
              </motion.button>
            ))}
          </div>
        ),
      },
      {
        title: "Pre-requirements check",
        subtitle: "Make sure you meet these requirements before proceeding",
        content: (
          <div className="tw-w-full tw-max-w-xl">
            <div className="tw-rounded-xl tw-bg-gray-50 tw-p-6">
              {formData.programType ? (
                <>
                  <div className="tw-mb-6">
                    <h3 className="tw-text-xl tw-font-semibold">
                      Requirements for{" "}
                      {duckLevels.find((p) => p.id === formData.programType)?.title}
                    </h3>
                    <p className="tw-mt-2 tw-text-sm tw-text-gray-500">
                      Please ensure you meet all required criteria before proceeding
                    </p>
                  </div>
                  <div className="tw-space-y-6">
                    <div className="tw-space-y-4">
                      {duckLevels
                        .find((p) => p.id === formData.programType)
                        ?.requirements.map((req, index) => (
                          <div key={index} className="tw-flex tw-items-start tw-gap-3">
                            <div className="tw-mt-1 tw-rounded-full tw-bg-white/10 tw-p-1">
                              <CheckCircle2
                                className={`tw-h-4 tw-w-4 ${
                                  req.importance === "required"
                                    ? "tw-text-primary-main"
                                    : "tw-text-white/40"
                                }`}
                              />
                            </div>
                            <div>
                              <div className="tw-flex tw-items-center tw-gap-2">
                                <p className="tw-font-medium">{req.title}</p>
                                {req.importance === "recommended" && (
                                  <span className="tw-rounded-full tw-bg-gray-200 tw-px-2 tw-py-0.5 tw-text-xs tw-text-gray-500">
                                    Recommended
                                  </span>
                                )}
                              </div>
                              <p className="tw-text-sm tw-text-gray-500">{req.description}</p>
                            </div>
                          </div>
                        ))}
                    </div>

                    <div className="tw-mt-8 tw-space-y-4">
                      <label className="tw-group tw-flex tw-cursor-pointer tw-items-start tw-gap-3">
                        <div className="tw-relative tw-mt-0.5 tw-h-5 tw-w-5 tw-flex-none">
                          <input
                            type="checkbox"
                            checked={formData.has_acknowledged_prereqs}
                            onChange={(e) => {
                              const newFormData = {
                                ...formData,
                                has_acknowledged_prereqs: e.target.checked,
                              };
                              setFormData(newFormData);
                              debouncedSave(newFormData);
                            }}
                            className="tw-peer tw-sr-only"
                          />
                          <div className="tw-absolute tw-inset-0 tw-rounded-md tw-border-2 tw-border-gray-200 tw-bg-gray-50 tw-transition-all group-hover:tw-border-gray-400 peer-checked:tw-border-primary-main peer-checked:tw-bg-primary-main peer-focus:tw-border-primary-main/70" />
                          <AnimatePresence>
                            {formData.has_acknowledged_prereqs && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.5 }}
                                transition={{ duration: 0.15 }}
                                className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-white"
                              >
                                <Check className="tw-h-3.5 tw-w-3.5 tw-stroke-[3]" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        <span className="tw-text-sm tw-text-gray-700 group-hover:tw-text-gray-900">
                          I confirm that I meet all the required criteria and understand the
                          recommended qualifications for this role.
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <div className="tw-py-8 tw-text-center">
                  <p className="tw-text-lg tw-text-gray-500">
                    Please select a program type first to view its requirements
                  </p>
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        title: "Learn about Sotravel",
        subtitle: "Watch this introduction video",
        content: (
          <VideoSection
            title="Understanding Sotravel's mission"
            description="Learn about our core values and what makes Sotravel unique"
          />
        ),
      },
      {
        title: "Let's test your understanding (1/4)",
        subtitle: quizQuestions[0].title,
        content: (
          <QuizQuestion
            questions={quizQuestions[0].questions}
            selectedAnswers={formData.quizAnswers}
            onSelect={(questionId, answerId) => {
              const newFormData = {
                ...formData,
                quizAnswers: { ...formData.quizAnswers, [questionId]: answerId },
                has_acknowledged_prereqs: formData.has_acknowledged_prereqs, // Keep for backward compatibility
              };
              setFormData(newFormData);
              debouncedSave(newFormData);
            }}
            startingNumber={1}
          />
        ),
      },
      {
        title: "Learn about trip leader role",
        subtitle: "Watch this guide on being a trip leader",
        content: (
          <VideoSection
            title="The role of a trip leader"
            description="Understand what it means to be a Sotravel trip leader and your key responsibilities"
          />
        ),
      },
      {
        title: "Let's test your understanding (2/4)",
        subtitle: quizQuestions[1].title,
        content: (
          <QuizQuestion
            questions={quizQuestions[1].questions}
            selectedAnswers={formData.quizAnswers}
            onSelect={(questionId, answerId) => {
              const newFormData = {
                ...formData,
                quizAnswers: { ...formData.quizAnswers, [questionId]: answerId },
                has_acknowledged_prereqs: formData.has_acknowledged_prereqs, // Keep for backward compatibility
              };
              setFormData(newFormData);
              debouncedSave(newFormData);
            }}
            startingNumber={quizQuestions[0].questions.length + 1}
          />
        ),
      },
      {
        title: "Learn about trip management",
        subtitle: "Watch this guide on managing trips effectively",
        content: (
          <VideoSection
            title="Trip management essentials"
            description="Learn the key aspects of managing successful Sotravel trips"
          />
        ),
      },
      {
        title: "Let's test your understanding (3/4)",
        subtitle: quizQuestions[2].title,
        content: (
          <QuizQuestion
            questions={quizQuestions[2].questions}
            selectedAnswers={formData.quizAnswers}
            onSelect={(questionId, answerId) => {
              const newFormData = {
                ...formData,
                quizAnswers: { ...formData.quizAnswers, [questionId]: answerId },
                has_acknowledged_prereqs: formData.has_acknowledged_prereqs, // Keep for backward compatibility
              };
              setFormData(newFormData);
              debouncedSave(newFormData);
            }}
            startingNumber={
              quizQuestions[0].questions.length + quizQuestions[1].questions.length + 1
            }
          />
        ),
      },
      {
        title: "Learn about group dynamics",
        subtitle: "Watch this guide on managing group dynamics",
        content: (
          <VideoSection
            title="Understanding group dynamics"
            description="Learn how to facilitate positive group interactions and handle different personalities"
          />
        ),
      },
      {
        title: "Let's test your understanding (4/4)",
        subtitle: quizQuestions[3].title,
        content: (
          <QuizQuestion
            questions={quizQuestions[3].questions}
            selectedAnswers={formData.quizAnswers}
            onSelect={(questionId, answerId) => {
              const newFormData = {
                ...formData,
                quizAnswers: { ...formData.quizAnswers, [questionId]: answerId },
                has_acknowledged_prereqs: formData.has_acknowledged_prereqs, // Keep for backward compatibility
              };
              setFormData(newFormData);
              debouncedSave(newFormData);
            }}
            startingNumber={
              quizQuestions[0].questions.length +
              quizQuestions[1].questions.length +
              quizQuestions[2].questions.length +
              1
            }
          />
        ),
      },
      {
        title: "Quiz Results",
        subtitle: "Let's review your understanding",
        content: (
          <QuizResults
            quizQuestions={quizQuestions}
            answers={formData.quizAnswers}
            onRetake={handleRetakeQuiz}
          />
        ),
      },
      {
        title: "Junior duck achieved",
        subtitle: "Congratulations on becoming a Sotravel junior duck!",
        content: <SuccessView />,
      },
    ];
  }, [formData, quizQuestions, handleRetakeQuiz, debouncedSave]);

  const canProceed = useCallback(() => {
    switch (formData.currentStep) {
      case 0: // Program Type
        return formData.programType !== "";
      case 1: // Pre-requirements
        return formData.programType !== "" && formData.has_acknowledged_prereqs;
      case 2: // Video 1 - Understanding Sotravel
        return true;
      case 3: // Quiz 1 - Understanding Sotravel
        return quizQuestions[0].questions.every((q) => formData.quizAnswers[q.id] !== undefined);
      case 4: // Video 2 - Trip Leader Role
        return true;
      case 5: // Quiz 2 - Trip Leader Role
        return quizQuestions[1].questions.every((q) => formData.quizAnswers[q.id] !== undefined);
      case 6: // Video 3 - Trip Management
        return true;
      case 7: // Quiz 3 - Trip Management
        return quizQuestions[2].questions.every((q) => formData.quizAnswers[q.id] !== undefined);
      case 8: // Video 4 - Group Dynamics
        return true;
      case 9: // Quiz 4 - Group Dynamics
        return quizQuestions[3].questions.every((q) =>
          q.multiSelect
            ? (formData.quizAnswers[q.id]?.length || 0) > 0
            : formData.quizAnswers[q.id] !== undefined
        );
      case 10: // Quiz Results
        // Calculate if all answers are correct
        const totalQuestions = quizQuestions.reduce(
          (acc, section) => acc + section.questions.length,
          0
        );
        const correctAnswers = quizQuestions.reduce((acc, section) => {
          return (
            acc +
            section.questions.reduce((sectionAcc, question) => {
              const isCorrect = question.multiSelect
                ? JSON.stringify(formData.quizAnswers[question.id]?.sort()) ===
                  JSON.stringify(question.correctAnswers.sort())
                : formData.quizAnswers[question.id] === question.correctAnswers[0];
              return sectionAcc + (isCorrect ? 1 : 0);
            }, 0)
          );
        }, 0);
        return correctAnswers === totalQuestions; // Only proceed if all answers are correct
      default:
        return true;
    }
  }, [formData, quizQuestions]);

  const handleNext = useCallback(() => {
    if (canProceed()) {
      const nextStep = Math.min(formData.currentStep + 1, steps.length - 1);

      // If completing the application
      if (formData.currentStep === 10) {
        const totalQuestions = quizQuestions.reduce(
          (acc, section) => acc + section.questions.length,
          0
        );
        const correctAnswers = quizQuestions.reduce((acc, section) => {
          return (
            acc +
            section.questions.reduce((sectionAcc, question) => {
              const isCorrect = question.multiSelect
                ? JSON.stringify(formData.quizAnswers[question.id]?.sort()) ===
                  JSON.stringify(question.correctAnswers.sort())
                : formData.quizAnswers[question.id] === question.correctAnswers[0];
              return sectionAcc + (isCorrect ? 1 : 0);
            }, 0)
          );
        }, 0);

        const quizScore = Math.round((correctAnswers / totalQuestions) * 100);
        // Save with completion data
        const newFormData = {
          ...formData,
          quizScore,
          currentStep: nextStep,
          has_acknowledged_prereqs: formData.has_acknowledged_prereqs,
        };
        setFormData(newFormData);
        debouncedSave(newFormData, true);
      } else {
        // Save progress on every continue click
        const newFormData = {
          ...formData,
          currentStep: nextStep,
          has_acknowledged_prereqs: formData.has_acknowledged_prereqs,
        };
        setFormData(newFormData);
        debouncedSave(newFormData);
      }

      setFormData((prev) => ({ ...prev, currentStep: nextStep }));
    }
  }, [canProceed, formData, steps.length, quizQuestions, debouncedSave]);

  const handlePrevious = useCallback(() => {
    const prevStep = Math.max(formData.currentStep - 1, 0);
    const newFormData = {
      ...formData,
      currentStep: prevStep,
    };
    setFormData(newFormData);
    // Save immediately when going back to ensure the step is persisted
    saveProgress(newFormData);
  }, [saveProgress, formData]);

  // Check for existing applications on mount
  useEffect(() => {
    const checkExistingApplication = async () => {
      try {
        const { data } = await getDuckApplications();
        const activeApplication = data.find(
          (app) => app.status === "pending" || app.status === "in_progress"
        );

        if (activeApplication) {
          setFormData({
            programType: activeApplication.duck_type,
            quizAnswers: activeApplication.quiz_answers || {},
            has_acknowledged_prereqs: activeApplication.has_acknowledged_prereqs || false,
            currentStep: activeApplication.last_step || 0,
            showingResults: false,
          });
        }
      } catch (error) {
        console.error("Error checking existing application:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingApplication();
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      } else if (
        e.key === "Backspace" &&
        e.target.tagName !== "INPUT" &&
        e.target.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        handlePrevious();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleNext, handlePrevious]);

  if (isLoading) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-w-full tw-max-w-lg tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-8 tw-shadow-xl">
          <div className="tw-mb-6 tw-flex tw-justify-center">
            <div className="tw-h-24 tw-w-24 tw-animate-pulse tw-rounded-full tw-bg-gray-200" />
          </div>
          <div className="tw-space-y-4">
            <div className="tw-mx-auto tw-h-8 tw-w-3/4 tw-animate-pulse tw-rounded tw-bg-gray-200" />
            <div className="tw-mx-auto tw-h-4 tw-w-5/6 tw-animate-pulse tw-rounded tw-bg-gray-200" />
            <div className="tw-mx-auto tw-h-4 tw-w-4/6 tw-animate-pulse tw-rounded tw-bg-gray-200" />
          </div>
          <div className="tw-mt-8 tw-flex tw-justify-center">
            <div className="tw-h-10 tw-w-48 tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
          </div>
          <p className="tw-mt-6 tw-text-center tw-text-sm tw-text-gray-500">
            Loading your application...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-gray-900">
      {/* Back Navigation */}
      <div className="tw-fixed tw-left-4 tw-top-4 tw-z-50 md:tw-left-8 md:tw-top-8">
        <button
          onClick={() => navigate("/our-leader-programme")}
          className="tw-group tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-gray-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-transition-all hover:tw-bg-gray-200 hover:tw-text-gray-900"
        >
          <ArrowLeft className="tw-h-4 tw-w-4 tw-transition-transform group-hover:-tw-translate-x-1" />
          Back to overview
        </button>
      </div>

      {/* Progress Bar */}
      <div className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-h-1 tw-bg-gray-100">
        <motion.div
          className="tw-h-full tw-bg-primary-main"
          initial={{ width: "0%" }}
          animate={{ width: `${((formData.currentStep + 1) / steps.length) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Main Content */}
      <div className="tw-container tw-mx-auto tw-min-h-screen tw-px-4 tw-py-20 tw-pb-32 md:tw-px-8 md:tw-pb-40">
        <AnimatePresence mode="wait">
          <motion.div
            key={formData.currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="tw-flex tw-min-h-[60vh] tw-flex-col tw-items-center"
          >
            {/* Step Title */}
            <div className="tw-mb-12 tw-text-center">
              <h1 className="tw-mb-3 tw-text-4xl tw-font-bold md:tw-text-5xl">
                {steps[formData.currentStep].title}
              </h1>
              <p className="tw-text-lg tw-text-gray-500">{steps[formData.currentStep].subtitle}</p>
            </div>

            {/* Step Content */}
            {steps[formData.currentStep].content}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-border-t tw-border-gray-200 tw-bg-white/80 tw-backdrop-blur-sm">
          <div className="tw-container tw-mx-auto tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-4">
            <button
              onClick={handlePrevious}
              className={`tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-6 tw-py-3 tw-transition-all ${
                formData.currentStep === 0
                  ? "tw-pointer-events-none tw-opacity-0"
                  : "tw-opacity-100 hover:tw-bg-white/10"
              }`}
            >
              <ArrowLeft className="tw-h-5 tw-w-5" />
              Back
            </button>

            <div className="tw-text-sm tw-text-gray-500">
              Step {formData.currentStep + 1} of {steps.length}
            </div>

            {formData.currentStep === steps.length - 1 ? (
              <button
                onClick={() => navigate("/profile/duck")}
                className="tw-rounded-full tw-bg-white tw-px-8 tw-py-3 tw-font-medium tw-text-black hover:tw-bg-white/90"
              >
                Go to duck page
              </button>
            ) : formData.currentStep === steps.length - 2 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-8 tw-py-3 tw-font-medium tw-transition-all ${
                  canProceed()
                    ? "tw-bg-primary-main tw-text-white hover:tw-bg-primary-main/90"
                    : "tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400"
                }`}
              >
                Submit Application
                <ArrowRight className="tw-h-5 tw-w-5" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-8 tw-py-3 tw-font-medium tw-transition-all ${
                  canProceed()
                    ? "tw-bg-primary-main tw-text-white hover:tw-bg-primary-main/90"
                    : "tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400"
                }`}
              >
                Continue
                <ArrowRight className="tw-h-5 tw-w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupLeaderApplication;
